import Grid from '@mui/material/Grid2';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ServiceVehiclesViewModel from '../../ViewModels/ServiceVehicles';
import getTimeIntervals from '../../../Utilities/Constants/HourTimeIntervals';
import SurveyDaysAdditionalDetailsViewModel from '../../ViewModels/SurveyDaysAdditionalDetailsViewModel';
import EditControl from '../EditControl';
import ServiceVehiclesTableRow from './ServiceVehicleTableRow';
import { APIContext } from '../../../Contexts/APIContext';
import { useContext, useMemo } from 'react';

interface SurveyTableProps {
    survey: ServiceVehiclesViewModel;
    landUse: string;
    readOnly: boolean;
    setReadOnly: React.Dispatch<React.SetStateAction<boolean>>;
    comment?: string;
    additionalDetails: SurveyDaysAdditionalDetailsViewModel;
    setValue: React.Dispatch<React.SetStateAction<ServiceVehiclesViewModel>>;
}

type Totals = {
    car: number;
    lgv: number;
    motc: number;
    ogv1: number;
    ogv2: number;
    total: number;
};

const toNum = (n: number | undefined) => (typeof n === 'number' && n >= 0 ? n : 0);
const isOutOfPeriodBand = (band: string | null | undefined, values: number[]) =>
    (!band || band.trim() === '' || band.includes('-1'))
    && values.every((value) => value === -1);
const normalizeBandValues = (values: number[], hasMotc: boolean) => {
    const expectedLength = hasMotc ? 5 : 4;
    const normalized = [...values];

    if (hasMotc && normalized.length === 4) {
        normalized.splice(2, 0, -1);
    }

    while (normalized.length < expectedLength) {
        normalized.push(-1);
    }

    return normalized.slice(0, expectedLength);
};

export default function ServiceVehiclesTable({ survey, landUse, readOnly, setReadOnly, comment, additionalDetails, setValue }: SurveyTableProps) {
    const HourTimeIntervals = getTimeIntervals(landUse);
    const { post, showMessage } = useContext(APIContext);
    let hourly = landUse !== "02";

    let hasMotc = false;

    const inboundTotals: Totals = { car: 0, lgv: 0, motc: 0, ogv1: 0, ogv2: 0, total: 0 };
    const outboundTotals: Totals = { car: 0, lgv: 0, motc: 0, ogv1: 0, ogv2: 0, total: 0 };

    if (survey.arrivals && survey.departures) {
        HourTimeIntervals.forEach((_, index) => {
            const arrivals =
                survey.arrivals?.[index * 2]?.split(' ').filter(str => str !== '').map(Number) || [];
            const departures =
                survey.departures?.[index * 2]?.split(' ').filter(str => str !== '').map(Number) || [];

            if (arrivals.length === 5 || departures.length === 5) hasMotc = true;

            if (arrivals.length === 5) {
                inboundTotals.car += toNum(arrivals[0]);
                inboundTotals.lgv += toNum(arrivals[1]);
                inboundTotals.motc += toNum(arrivals[2]);
                inboundTotals.ogv1 += toNum(arrivals[3]);
                inboundTotals.ogv2 += toNum(arrivals[4]);
            } else {
                inboundTotals.car += toNum(arrivals[0]);
                inboundTotals.lgv += toNum(arrivals[1]);
                inboundTotals.ogv1 += toNum(arrivals[2]);
                inboundTotals.ogv2 += toNum(arrivals[3]);
            }
            inboundTotals.total += arrivals.reduce((acc, v) => acc + toNum(v), 0);

            if (departures.length === 5) {
                outboundTotals.car += toNum(departures[0]);
                outboundTotals.lgv += toNum(departures[1]);
                outboundTotals.motc += toNum(departures[2]);
                outboundTotals.ogv1 += toNum(departures[3]);
                outboundTotals.ogv2 += toNum(departures[4]);
            } else {
                outboundTotals.car += toNum(departures[0]);
                outboundTotals.lgv += toNum(departures[1]);
                outboundTotals.ogv1 += toNum(departures[2]);
                outboundTotals.ogv2 += toNum(departures[3]);
            }
            outboundTotals.total += departures.reduce((acc, v) => acc + toNum(v), 0);
        });
    }

    const showMotc = hasMotc || !readOnly;



    const submit = () => {
        if (!survey.id) {
            showMessage('error', 'Servicing vehicle data is not available in the selected version.');
            return;
        }

        survey.comment = comment ?? '';
        survey.additionalDetails = additionalDetails;
        post('SurveyDays', `UpdateSurveyDaysServicingVehices`, survey, async (
        ) => {
            setReadOnly(true);
        });
    }

    const editableRows = useMemo(() => {
        if (!survey.arrivals || !survey.departures) {
            return [];
        }

        return HourTimeIntervals.map((time, index) => {
            const newIndex = hourly ? index * 2 : index;
            const arrivals = normalizeBandValues(
                survey.arrivals?.[newIndex]?.split(' ').filter(str => str !== '').map(Number) || [],
                showMotc
            );
            const departures = normalizeBandValues(
                survey.departures?.[newIndex]?.split(' ').filter(str => str !== '').map(Number) || [],
                showMotc
            );

            return (
                <ServiceVehiclesTableRow
                    key={time}
                    arrivals={arrivals}
                    departures={departures}
                    survey={survey}
                    time={time}
                    rowIndex={newIndex}
                    setValue={setValue}
                    readOnly={false}
                    hasMotc={showMotc}
                />
            );
        });
    }, [survey, HourTimeIntervals, hourly, setValue, showMotc]);

    const readOnlyRows = useMemo(() => {
        if (!survey.arrivals || !survey.departures) {
            return [];
        }

        const rows: JSX.Element[] = [];
        let emptyGroupStartTime = '';
        let emptyGroupEndTime = '';
        let emptyGroupRowIndex: number | null = null;

        const flushEmptyGroup = () => {
            if (emptyGroupRowIndex == null) {
                return;
            }

            const emptyValues = Array(hasMotc ? 5 : 4).fill(-1);
            rows.push(
                <ServiceVehiclesTableRow
                    key={`${emptyGroupStartTime}-${emptyGroupEndTime}`}
                    arrivals={emptyValues}
                    departures={emptyValues}
                    survey={survey}
                    time={`${emptyGroupStartTime} - ${emptyGroupEndTime}`}
                    rowIndex={emptyGroupRowIndex}
                    setValue={() => { }}
                    readOnly={true}
                    hasMotc={hasMotc}
                />
            );

            emptyGroupStartTime = '';
            emptyGroupEndTime = '';
            emptyGroupRowIndex = null;
        };

        HourTimeIntervals.forEach((time, index) => {
            const newIndex = hourly ? index * 2 : index;
            const arrivalBand = survey.arrivals?.[newIndex];
            const departureBand = survey.departures?.[newIndex];
            const arrivals = normalizeBandValues(
                arrivalBand?.split(' ').filter(str => str !== '').map(Number) || [],
                hasMotc
            );
            const departures = normalizeBandValues(
                departureBand?.split(' ').filter(str => str !== '').map(Number) || [],
                hasMotc
            );
            const isEmptyReadOnlyRow =
                isOutOfPeriodBand(arrivalBand, arrivals)
                && isOutOfPeriodBand(departureBand, departures);

            if (isEmptyReadOnlyRow) {
                if (emptyGroupRowIndex == null) {
                    emptyGroupStartTime = time.split('-')[0]?.trim() ?? time;
                    emptyGroupRowIndex = newIndex;
                }
                emptyGroupEndTime = time.split('-')[1]?.trim() ?? time;
                return;
            }

            flushEmptyGroup();

            rows.push(
                <ServiceVehiclesTableRow
                    key={time}
                    arrivals={arrivals}
                    departures={departures}
                    survey={survey}
                    time={time}
                    rowIndex={newIndex}
                    setValue={() => { }}
                    readOnly={true}
                    hasMotc={hasMotc}
                />
            );
        });

        flushEmptyGroup();
        return rows;
    }, [survey, HourTimeIntervals, hourly, hasMotc]);


    return (
        <Grid size={12}>
            <EditControl readOnly={readOnly} setReadOnly={setReadOnly} submit={submit} />

            <Typography variant="h6">{ }</Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell colSpan={showMotc ? 6 : 5} align="center">
                                Inbound
                            </TableCell>
                            <TableCell />
                            <TableCell colSpan={showMotc ? 6 : 5} align="center">
                                Outbound
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Time</TableCell>
                            <TableCell>Car ({inboundTotals.car})</TableCell>
                            <TableCell>LGV ({inboundTotals.lgv})</TableCell>
                            {showMotc && <TableCell>MOTC ({inboundTotals.motc})</TableCell>}
                            <TableCell>OGV1 ({inboundTotals.ogv1})</TableCell>
                            <TableCell>OGV2 ({inboundTotals.ogv2})</TableCell>
                            <TableCell>Total ({inboundTotals.total})</TableCell>
                            <TableCell />
                            <TableCell>Car ({outboundTotals.car})</TableCell>
                            <TableCell>LGV ({outboundTotals.lgv})</TableCell>
                            {showMotc && <TableCell>MOTC ({outboundTotals.motc})</TableCell>}
                            <TableCell>OGV1 ({outboundTotals.ogv1})</TableCell>
                            <TableCell>OGV2 ({outboundTotals.ogv2})</TableCell>
                            <TableCell>Total ({outboundTotals.total})</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {readOnly ? readOnlyRows : editableRows}
                    </TableBody>

                </Table>
            </TableContainer>
        </Grid>
    );
}
